// useTableCRUD.ts
import { useState, useEffect, SetStateAction } from "react"; // <-- Importa useEffect
import { tableService } from "./api";
import { normalizeId, sortByProperty } from "./utils";

export const useTableCRUD = <T extends { id: number; [key: string]: any }>(
    initialData: T[]
) => {
    // Esto inicializa el estado la primera vez, lo cual está bien.
    const [data, setData] = useState<T[]>(initialData);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);

    const updateLocalData = (newData: SetStateAction<T[]>) => setData(newData);

    useEffect(() => {
        setData(initialData ?? []);
    }, [initialData]);

    const createItem = async (
        url: string,
        itemData: any,
        keyProp: string,
        sortProp: string,
        opts?: { mutate?: boolean }
    ) => {
        setIsLoading(true);
        try {
            const response = await tableService.create(url, itemData);
            let newItem: T;

            if (response.data && response.data.item) {
                newItem = response.data.item;
            } else if (response.data && response.data.newId) {
                newItem = { ...itemData, [keyProp]: response.data.newId };
            } else {
                newItem = response.data;
            }

            if (opts?.mutate !== false && newItem) {
                setData((prev) => {
                    const next = sortByProperty(
                        [...prev, newItem as T],
                        sortProp as keyof T
                    );
                    return next;
                });
            }
            return response.data;
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const updateItem = async (
        url: string,
        id: number | string,
        itemData: any,
        keyProp: string,
        sortProp: string
    ) => {
        const normalizeId = (v: any) => (v == null ? "" : String(v));
        const dgroup = (label: string, fn: () => void) => {
            console.groupCollapsed(label);
            try {
                fn();
            } finally {
                console.groupEnd();
            }
        };
        const dlog = (...args: any[]) => console.log(...args);

        setIsLoading(true);
        try {
            const apiRes = await tableService.update(url, id, itemData);
            const raw = apiRes?.data;

            dgroup("[CRUD:updateItem] RESPONSE", () => {
                dlog("id param:", id, "keyProp:", keyProp);
                dlog("raw:", raw);
            });

            let updated: any = raw?.item ?? raw;

            if (updated == null || typeof updated !== "object") {
                throw new Error(
                    "updateItem: API no devolvió un objeto de item válido"
                );
            }

            if (updated[keyProp] == null) {
                const inferred = raw?.id ?? updated.id ?? id;
                updated = { ...updated, [keyProp]: inferred };
            }

            const targetId = normalizeId(id);

            setData((prev) => {
                const idx = prev.findIndex(
                    (it) => normalizeId((it as any)[keyProp]) === targetId
                );
                dgroup("[CRUD:updateItem] setData", () => {
                    dlog("prev.length:", prev.length, "match idx:", idx);
                    if (idx >= 0) {
                        dlog("before:", prev[idx]);
                        dlog("after:", updated);
                    } else {
                        dlog("no match found for id:", id);
                    }
                });

                return prev.map((it) =>
                    normalizeId((it as any)[keyProp]) === targetId
                        ? (updated as typeof it)
                        : it
                );
            });

            return {
                item: updated,
                id: updated[keyProp],
                raw,
            };
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    const deleteItem = async (
        url: string,
        id: number,
        keyProp: string,
        sortProp: string
    ) => {
        setIsLoading(true);
        try {
            await tableService.delete(url, id);

            const targetId = normalizeId(id);

            setData((prev) =>
                prev.filter((item) => normalizeId(item[keyProp]) !== targetId)
            );
        } catch (err) {
            setError(err as Error);
            throw err;
        } finally {
            setIsLoading(false);
        }
    };

    return {
        data,
        setData,
        isLoading,
        error,
        createItem,
        updateItem,
        deleteItem,
        updateLocalData,
    };
};
